import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import client from "@/lib/apiClient";
import { useLang } from "@/i18n";
import { SectionHeader, Loader, Lu, StatCard, fmtSAR } from "@/components/shared";

const BADGE = {
  top_kwh: { icon: "zap", ar: "الأكثر توفيراً", en: "Top saver" },
  fastest_improver: { icon: "rocket", ar: "الأسرع تحسناً", en: "Fastest improver" },
  most_efficient: { icon: "leaf", ar: "الأكفأ لكل غرفة", en: "Most efficient / room" },
};
const MEDAL = ["#F5B301", "#A8B3C2", "#CD7F32"];

export default function Leaderboard() {
  const { lang, t } = useLang();
  const L = lang === "ar";
  const [d, setD] = useState(null);
  useEffect(() => { client.get("/leaderboard").then((r) => setD(r.data)); }, []);
  if (!d) return <Loader />;
  const rows = d.rows; const max = Math.max(...rows.map((r) => r.savings_pct), 1);
  const podium = [rows[1], rows[0], rows[2]].filter(Boolean);

  return (
    <div>
      <SectionHeader icon="trophy" title={L ? "لوحة أبطال التوفير" : "Savings Leaderboard"} subtitle={L ? `ترتيب العقارات حسب توفير الطاقة · ${d.month}` : `Properties ranked by energy savings · ${d.month}`} />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-6">
        <StatCard icon="zap" label={L ? "كيلوواط موفّرة (الكل)" : "Total kWh saved"} value={fmtSAR(d.totals.saved_kwh)} accent="#1E5EFF" testid="lb-total-kwh" />
        <StatCard icon="wallet" label={L ? "ريال موفّر" : "SAR saved"} value={fmtSAR(d.totals.saved_sar)} accent="#00A69E" />
        <StatCard icon="tree-pine" label={L ? "كجم CO₂ مُجنَّبة" : "kg CO₂ avoided"} value={fmtSAR(d.totals.co2_kg)} accent="#10B981" />
      </div>

      {/* Podium */}
      <div className="md-card p-6 mb-6 overflow-hidden relative" data-testid="lb-podium">
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(60% 80% at 50% 100%, rgba(0,71,255,0.12), transparent)" }} />
        <div className="relative grid grid-cols-3 gap-3 items-end max-w-3xl mx-auto">
          {podium.map((r) => {
            const h = r.rank === 1 ? 150 : r.rank === 2 ? 110 : 85;
            return (
              <motion.div key={r.property.id} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 * r.rank, duration: 0.5 }} className="flex flex-col items-center gap-2">
                <div className="w-12 h-12 rounded-full flex items-center justify-center font-extrabold text-lg shadow-lg" style={{ background: MEDAL[r.rank - 1], color: "#0B1A4A" }}>{r.rank}</div>
                <p className="text-sm font-bold text-foreground text-center leading-tight">{r.property[L ? "name_ar" : "name_en"]}</p>
                <p className="text-2xl font-extrabold font-mono text-primary">{r.savings_pct}%</p>
                <div className="w-full rounded-t-xl md-gold-gradient opacity-90" style={{ height: h, opacity: r.rank === 1 ? 1 : 0.55 }} />
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Ranked list */}
      <div className="space-y-3">
        {rows.map((r, i) => (
          <motion.div key={r.property.id} initial={{ opacity: 0, x: L ? 20 : -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 * i }}
            data-testid={`lb-row-${r.property.id}`} className="md-card p-4 flex flex-wrap items-center gap-4">
            <span className="w-10 h-10 rounded-xl flex items-center justify-center font-extrabold font-mono text-lg shrink-0"
              style={{ background: i < 3 ? MEDAL[i] : "hsl(var(--muted))", color: i < 3 ? "#0B1A4A" : "hsl(var(--foreground))" }}>{r.rank}</span>
            <div className="flex-1 min-w-[200px]">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-bold text-foreground">{r.property[L ? "name_ar" : "name_en"]}</p>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-foreground/5 text-muted-foreground">{r.property[L ? "city_ar" : "city_en"]} · {r.rooms} {t("rooms")}</span>
                {r.badges.map((b) => (
                  <span key={b} data-testid={`lb-badge-${b}`} className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/25">
                    <Lu name={BADGE[b].icon} className="w-3 h-3" />{BADGE[b][lang]}
                  </span>
                ))}
              </div>
              <div className="mt-2 h-2 rounded-full bg-foreground/5 overflow-hidden">
                <motion.div initial={{ width: 0 }} animate={{ width: `${(r.savings_pct / max) * 100}%` }} transition={{ duration: 0.8, delay: 0.1 * i }} className="h-full md-gold-gradient rounded-full" />
              </div>
            </div>
            <div className="grid grid-cols-4 gap-4 text-center shrink-0">
              {[[`${r.savings_pct}%`, L ? "التوفير" : "Savings"], [fmtSAR(r.saved_kwh), "kWh"], [fmtSAR(r.saved_sar), t("sar")],
                [`${r.trend_pct >= 0 ? "+" : ""}${r.trend_pct}`, L ? "الاتجاه (7ي)" : "Trend (7d)"]].map(([v, l], k) => (
                <div key={l}><p className={`font-mono font-bold ${k === 3 ? (r.trend_pct >= 0 ? "text-emerald-500" : "text-rose-500") : "text-foreground"}`}>{v}</p><p className="text-[10px] text-muted-foreground">{l}</p></div>
              ))}
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
